<template>
  <div class="page-template" :class="[maxWidthClass, paddingClass]">
    <!-- ページヘッダー領域 -->
    <header class="page-header">
      <slot name="header" />
    </header>

    <!-- タブナビゲーション領域 -->
    <nav class="navigation-area" v-if="$slots.navigation">
      <slot name="navigation" />
    </nav>

    <!-- メインコンテンツ領域 -->
    <main class="main-content">
      <slot name="content" />
    </main>

    <!-- フッター領域（オプション） -->
    <footer class="page-footer" v-if="$slots.footer">
      <slot name="footer" />
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
/**
 * PageTemplate - ページテンプレートコンポーネント (Template)
 *
 * アプリケーション全体で使用される再利用可能なページレイアウトテンプレートです。
 *
 * アトミックデザインのTemplates層として、以下の責務を持ちます：
 * - ページ全体のレイアウト構造の定義
 * - Organismsの配置とスペーシングの管理
 * - レスポンシブデザインの基盤提供
 * - 一貫したページ構造の保証
 *
 * スロット構造:
 * - header: ページタイトルやブレッドクラムなどのヘッダーコンテンツ
 * - navigation: タブやナビゲーションメニュー
 * - content: メインコンテンツエリア
 * - footer: フッターコンテンツ（オプション）
 */

const props = defineProps({
  /**
   * ページの最大幅制限
   * 'sm': 小（640px）
   * 'md': 中（768px）
   * 'lg': 大（1024px）
   * 'xl': 特大（1280px）
   * 'full': 制限なし
   */
  maxWidth: {
    type: String,
    default: 'lg',
    validator: (value) => {
      return ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    }
  },
  /**
   * ページのパディング
   * 'sm': 小
   * 'md': 標準
   * 'lg': 大
   */
  padding: {
    type: String,
    default: 'md',
    validator: (value) => {
      return ['sm', 'md', 'lg'].includes(value)
    }
  }
})

const maxWidthClass = computed(() => ({
  'max-w-sm': props.maxWidth === 'sm',
  'max-w-md': props.maxWidth === 'md',
  'max-w-4xl': props.maxWidth === 'lg',
  'max-w-6xl': props.maxWidth === 'xl',
  'max-w-full': props.maxWidth === 'full'
}))

const paddingClass = computed(() => ({
  'p-4': props.padding === 'sm',
  'p-6': props.padding === 'md',
  'p-8': props.padding === 'lg'
}))
</script>

<style scoped>
.page-template {
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: 1.5rem;
}

.navigation-area {
  margin-bottom: 1rem;
}

.main-content {
  flex: 1;
  min-height: 0; /* フレックスボックスのオーバーフロー対策 */
}

.page-footer {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

/* 最大幅制限クラス */
.max-w-sm {
  max-width: 640px;
}

.max-w-md {
  max-width: 768px;
}

.max-w-4xl {
  max-width: 896px;
}

.max-w-6xl {
  max-width: 1152px;
}

.max-w-full {
  max-width: 100%;
}

/* パディングクラス */
.p-4 {
  padding: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-8 {
  padding: 2rem;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .page-template {
    padding: 1rem;
  }

  .page-header {
    margin-bottom: 1rem;
  }

  .navigation-area {
    margin-bottom: 0.75rem;
  }
}
</style>
